import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import s from "./ui.module.css";

interface Hinweis {
  id: number;
  text: string;
  fehler?: boolean;
  rueckgaengig?: () => void;
  /** Ein weiterer Knopf im Hinweis, zum Beispiel „Öffnen“ nach dem Anlegen */
  aktion?: { label: string; ausfuehren: () => void };
}

interface HinweisKontext {
  zeigen: (text: string, optionen?: { fehler?: boolean; rueckgaengig?: () => void; aktion?: { label: string; ausfuehren: () => void } }) => void;
}

const Kontext = createContext<HinweisKontext | null>(null);

function istModal(d: HTMLDialogElement): boolean {
  try {
    return d.matches(":modal");
  } catch {
    return true;
  }
}

/** Der zuletzt geöffnete modale Dialog. Alles außerhalb ist dann nicht bedienbar, also kommen die Hinweise dort hinein,
 *  sonst ginge „Rückgängig“ im Aufgaben-Dialog nicht (AUFGABEN-LISTE.md, Abschnitt 4). */
function useOffenerDialog(): HTMLElement | null {
  const [ziel, setZiel] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const stapel: HTMLDialogElement[] = [];
    function pruefen() {
      const offen = [...document.querySelectorAll("dialog")].filter((d) => d.open && istModal(d));
      for (let i = stapel.length - 1; i >= 0; i--) if (!offen.includes(stapel[i] as HTMLDialogElement)) stapel.splice(i, 1);
      for (const d of offen) if (!stapel.includes(d)) stapel.push(d);
      setZiel(stapel.at(-1) ?? null);
    }
    const beobachter = new MutationObserver(pruefen);
    beobachter.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["open"] });
    pruefen();
    return () => beobachter.disconnect();
  }, []);
  return ziel;
}

/** Kurze Hinweise unten. Mit Rückgängig 10 Sekunden lang (KONZEPT.md, Zustände). Bei offenem Dialog liegen sie darin. */
export function HinweisAnbieter({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<Hinweis[]>([]);
  const naechste = useRef(1);
  const weg = useCallback((id: number) => setListe((l) => l.filter((h) => h.id !== id)), []);
  const zeigen = useCallback<HinweisKontext["zeigen"]>(
    (text, optionen = {}) => {
      const id = naechste.current++;
      setListe((l) => [...l.slice(-2), { id, text, ...optionen }]);
      window.setTimeout(() => weg(id), optionen.rueckgaengig || optionen.aktion ? 10000 : optionen.fehler ? 8000 : 5000);
    },
    [weg],
  );
  const wert = useMemo(() => ({ zeigen }), [zeigen]);
  const dialog = useOffenerDialog();
  const bereich = (
    <div className={s.hinweise} aria-live="polite" role="status">
      {liste.map((h) => (
        <div key={h.id} className={[s.hinweis, h.fehler && s.hinweisFehler].filter(Boolean).join(" ")}>
          <span>{h.text}</span>
          {h.rueckgaengig ? (
            <button
              type="button"
              onClick={() => {
                h.rueckgaengig?.();
                weg(h.id);
              }}
            >
              Rückgängig
            </button>
          ) : null}
          {h.aktion ? (
            <button
              type="button"
              onClick={() => {
                h.aktion?.ausfuehren();
                weg(h.id);
              }}
            >
              {h.aktion.label}
            </button>
          ) : null}
          <button type="button" aria-label="Hinweis schließen" onClick={() => weg(h.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
  return (
    <Kontext.Provider value={wert}>
      {children}
      {createPortal(bereich, dialog ?? document.body)}
    </Kontext.Provider>
  );
}

export function useHinweis(): HinweisKontext {
  const k = useContext(Kontext);
  if (!k) throw new Error("useHinweis nur innerhalb von HinweisAnbieter.");
  return k;
}
